// The knowledge base page viewer (Session BAV). One page of a document, shown full screen: the picture of the page
// when the platform holds one (a manual's wiring diagram, a scanned fault table), else the page's own text. Used by
// BOTH the office (a source card on the Knowledge base Query tab) and the engineer app (the Fault Assistant's "Find
// the page" results and its answer's source cards), so it is a plain self-contained overlay, mobile first.
//
// READ ONLY. The only controls are zoom, page to page, Open original and Back. Nothing here writes anything.
//
// THE ZOOM is hand-built on pointer events (no new dependency): two fingers pinch and pan, one finger (or a mouse)
// drags the picture once it is zoomed in, a double tap or double click toggles between 1x and 2.5x at the point
// tapped, and the +, - and reset buttons work about the centre. The scale is clamped between 1x and 6x and the
// picture is kept inside its frame. It is a CSS translate plus scale on the image, with touch-action none on the
// frame so the browser does not scroll or zoom the page underneath.
//
// SELF-CONTAINED OVERLAY, deliberately not ModalShell (an office component that switches on the office density
// system): rendered into document.body through a portal, its own Escape handler, focus moved onto the panel on open
// and restored on close, and the page's scroll locked while it is open. A caller that wants the page behind it out of
// the tab order marks its own content inert (the Fault Assistant does).
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { knowledgePageImageUrl, pagePlaceLabel, type KnowledgePageView } from "../lib/knowledgeTypes";

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 6;
export const DOUBLE_TAP_ZOOM = 2.5;
const BUTTON_ZOOM_STEP = 1.5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP_PX = 30;

export interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: ZoomState = { scale: 1, x: 0, y: 0 };

/** Clamp a scale into the allowed range. */
export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
}

/**
 * Zoom to `nextScale` keeping the point (px, py), in the frame's own coordinates, where it is on screen, then keep the
 * picture inside a frame of width w and height h. Pure, so the arithmetic is tested without a browser.
 */
export function zoomAt(state: ZoomState, nextScale: number, px: number, py: number, w: number, h: number): ZoomState {
  const scale = clampScale(nextScale);
  const ratio = scale / state.scale;
  return keepInFrame({ scale, x: px - (px - state.x) * ratio, y: py - (py - state.y) * ratio }, w, h);
}

/** Keep a scaled picture (which fills a w by h frame at 1x) covering its frame: no gap at any edge. */
export function keepInFrame(state: ZoomState, w: number, h: number): ZoomState {
  const scale = clampScale(state.scale);
  if (scale <= MIN_ZOOM) return { ...IDENTITY };
  const minX = w - w * scale;
  const minY = h - h * scale;
  return {
    scale,
    x: Math.min(0, Math.max(minX, state.x)),
    y: Math.min(0, Math.max(minY, state.y)),
  };
}

type LoadState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; page: KnowledgePageView };

export function KnowledgePageViewer({
  documentId,
  pageNumber,
  onClose,
  onNavigate,
  closeLabel = "Back to results",
}: {
  documentId: string;
  pageNumber: number;
  onClose: () => void;
  // Told when the reader moves to another page. The viewer loads the new page itself either way, so a caller that
  // does not track the page need not pass it.
  onNavigate?: (pageNumber: number) => void;
  closeLabel?: string;
}) {
  const [current, setCurrent] = useState(pageNumber);
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [zoom, setZoom] = useState<ZoomState>(IDENTITY);

  const panelRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  // A caller that moves the page itself (through onNavigate) is followed.
  useEffect(() => {
    setCurrent(pageNumber);
  }, [pageNumber]);

  // onClose held in a ref so the open effect runs once per open, whatever the caller passes each render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoad({ kind: "loading" });
    setImageFailed(false);
    setZoom(IDENTITY);
    pointers.current.clear();
    lastTap.current = null;
    api.knowledge
      .page(documentId, current)
      .then((res) => {
        if (active) setLoad({ kind: "ready", page: res.page });
      })
      .catch(() => {
        if (active) setLoad({ kind: "error" });
      });
    return () => {
      active = false;
    };
  }, [documentId, current, attempt]);

  function goTo(n: number | null) {
    if (n == null) return;
    setCurrent(n);
    onNavigate?.(n);
  }

  const frameSize = useCallback(() => {
    const el = frameRef.current;
    return { w: el?.clientWidth ?? 0, h: el?.clientHeight ?? 0 };
  }, []);

  // The point (x, y) of a client position, in the frame's own coordinates.
  const framePoint = useCallback((clientX: number, clientY: number) => {
    const rect = frameRef.current?.getBoundingClientRect();
    return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
  }, []);

  function zoomBy(factor: number) {
    const { w, h } = frameSize();
    setZoom((z) => zoomAt(z, z.scale * factor, w / 2, h / 2, w, h));
  }

  function toggleAt(clientX: number, clientY: number) {
    const { w, h } = frameSize();
    const p = framePoint(clientX, clientY);
    setZoom((z) => (z.scale > MIN_ZOOM ? { ...IDENTITY } : zoomAt(z, DOUBLE_TAP_ZOOM, p.x, p.y, w, h)));
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    pointers.current.set(e.pointerId, framePoint(e.clientX, e.clientY));
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // A pointer that cannot be captured still works while it stays over the frame.
    }
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const next = framePoint(e.clientX, e.clientY);
    const { w, h } = frameSize();
    if (pointers.current.size >= 2) {
      // Two fingers: the change in their spread scales the picture about their midpoint, and the midpoint's own
      // movement pans it.
      const others = [...pointers.current.entries()].filter(([id]) => id !== e.pointerId);
      const other = others[0]?.[1];
      pointers.current.set(e.pointerId, next);
      if (!other) return;
      const before = Math.hypot(prev.x - other.x, prev.y - other.y);
      const after = Math.hypot(next.x - other.x, next.y - other.y);
      if (before <= 0) return;
      const midBefore = { x: (prev.x + other.x) / 2, y: (prev.y + other.y) / 2 };
      const midAfter = { x: (next.x + other.x) / 2, y: (next.y + other.y) / 2 };
      setZoom((z) => {
        const scaled = zoomAt(z, z.scale * (after / before), midBefore.x, midBefore.y, w, h);
        return keepInFrame({ ...scaled, x: scaled.x + (midAfter.x - midBefore.x), y: scaled.y + (midAfter.y - midBefore.y) }, w, h);
      });
      return;
    }
    pointers.current.set(e.pointerId, next);
    if (zoomRef.current.scale <= MIN_ZOOM) return;
    setZoom((z) => keepInFrame({ ...z, x: z.x + (next.x - prev.x), y: z.y + (next.y - prev.y) }, w, h));
  }

  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    const wasPinching = pointers.current.size >= 2;
    pointers.current.delete(e.pointerId);
    // A mouse double click is handled by onDoubleClick; the double TAP is a touch (or pen) gesture.
    if (e.pointerType === "mouse" || wasPinching || e.type === "pointercancel") return;
    const now = Date.now();
    const last = lastTap.current;
    if (last && now - last.at < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < DOUBLE_TAP_SLOP_PX) {
      lastTap.current = null;
      toggleAt(e.clientX, e.clientY);
      return;
    }
    lastTap.current = { at: now, x: e.clientX, y: e.clientY };
  }

  const page = load.kind === "ready" ? load.page : null;
  const showImage = Boolean(page?.hasImage) && !imageFailed;
  const place = page ? pagePlaceLabel(page.pageNumber, page.label) : "";
  const placeLine = page && !page.label && page.pageCount ? `${place} of ${page.pageCount}` : place;
  const zoomPercent = Math.round(zoom.scale * 100);

  const navBtn =
    "inline-flex min-h-11 min-w-11 items-center justify-center rounded-ele border border-ele-purple-accent px-4 font-menu text-sm tracking-wide text-ele-strong transition hover:border-ele-blue disabled:opacity-40";
  const zoomBtn =
    "inline-flex h-11 min-w-11 items-center justify-center rounded-ele border border-ele-purple-accent bg-ele-surface px-3 font-heading text-lg text-ele-strong transition hover:border-ele-blue disabled:opacity-40";

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[var(--ele-shell)] text-ele-body">
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={page ? `Page viewer: ${page.fileName}` : "Page viewer"}
        className="mx-auto flex min-h-dvh max-w-4xl flex-col p-4 outline-none"
      >
        <div className="flex items-start gap-3 border-b border-ele-purple-accent pb-3">
          <div className="min-w-0 flex-1">
            <h2 className="break-words font-heading text-lg tracking-wide text-ele-strong">{page?.fileName ?? "Document page"}</h2>
            {page && (
              <p className="mt-0.5 flex flex-wrap items-center gap-2 font-menu text-xs uppercase tracking-wide text-ele-body">
                {page.manufacturer && <span>{page.manufacturer}</span>}
                {placeLine && <span>{placeLine}</span>}
                {page.isDiagram && (
                  <span className="rounded border border-ele-blue px-2 py-0.5 text-[11px] text-ele-blue">Diagram</span>
                )}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className={`${navBtn} flex-none`}>
            {closeLabel}
          </button>
        </div>

        {load.kind === "loading" && (
          <div role="status" aria-live="polite" className="py-12 text-center text-base text-ele-body">
            Loading page...
          </div>
        )}

        {load.kind === "error" && (
          <div role="alert" className="mt-6 rounded-ele border border-ele-danger bg-ele-surface p-4 text-center">
            <p className="text-base text-ele-strong">This page could not be loaded. Check your signal and try again.</p>
            <button type="button" onClick={() => setAttempt((a) => a + 1)} className={`${navBtn} mt-3`}>
              Retry
            </button>
          </div>
        )}

        {page && (
          <div className="mt-4 flex flex-1 flex-col">
            {showImage ? (
              <>
                <div
                  ref={frameRef}
                  data-testid="page-zoom-frame"
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  onDoubleClick={(e) => toggleAt(e.clientX, e.clientY)}
                  className="relative overflow-hidden rounded-ele border border-ele-hairline bg-white"
                  style={{ touchAction: "none", cursor: zoom.scale > MIN_ZOOM ? "grab" : "zoom-in" }}
                >
                  <img
                    src={knowledgePageImageUrl(page.documentId, page.pageNumber)}
                    alt={`${place || "Page"} of ${page.fileName}`}
                    draggable={false}
                    onError={() => setImageFailed(true)}
                    width={page.imageWidth ?? undefined}
                    height={page.imageHeight ?? undefined}
                    className="block h-auto w-full select-none"
                    style={{
                      transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
                      transformOrigin: "0 0",
                      willChange: "transform",
                    }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <button type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / BUTTON_ZOOM_STEP)} disabled={zoom.scale <= MIN_ZOOM} className={zoomBtn}>
                    &minus;
                  </button>
                  <button type="button" aria-label="Reset zoom" onClick={() => setZoom({ ...IDENTITY })} className={`${zoomBtn} font-menu text-sm`}>
                    {zoomPercent}%
                  </button>
                  <button type="button" aria-label="Zoom in" onClick={() => zoomBy(BUTTON_ZOOM_STEP)} disabled={zoom.scale >= MAX_ZOOM} className={zoomBtn}>
                    +
                  </button>
                </div>
                <p className="mt-1 text-center text-xs text-ele-body opacity-70">Pinch or double tap to zoom. Drag to move around.</p>
                {page.visionDescription && (
                  <p className="mt-3 text-sm text-ele-body">
                    <span className="font-semibold text-ele-strong">What this diagram shows: </span>
                    {page.visionDescription}
                  </p>
                )}
              </>
            ) : (
              <>
                {page.text ? (
                  <div className="whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-surface p-4 text-base leading-relaxed text-ele-strong">
                    {page.text}
                  </div>
                ) : (
                  <p className="rounded-ele border border-ele-hairline bg-ele-surface p-4 text-base text-ele-body">
                    No text was read from this page. Open the original to see it.
                  </p>
                )}
                {page.visionDescription && (
                  <p className="mt-3 text-sm text-ele-body">
                    <span className="font-semibold text-ele-strong">What this diagram shows: </span>
                    {page.visionDescription}
                  </p>
                )}
              </>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <button type="button" onClick={() => goTo(page.prevPage)} disabled={page.prevPage == null} className={navBtn}>
                Previous page
              </button>
              {page.openOriginalUrl && (
                <a href={page.openOriginalUrl} target="_blank" rel="noopener noreferrer" className={`${navBtn} text-ele-blue`}>
                  Open original
                </a>
              )}
              <button type="button" onClick={() => goTo(page.nextPage)} disabled={page.nextPage == null} className={navBtn}>
                Next page
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
